import { useState } from 'react';
import type { Deal, SCData, TabState } from '../../types';
import { buildSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
type DealViewMode = 'cards' | 'table' | 'tile' | 'gallery' | 'waterfall';

/* ─── SVG icons for the view switcher ──────────────────────────────────── */

function IconCards() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-243" data-mp="156x5lfnumrvm">
      <rect x="1" y="1" width="6" height="6" rx="1" data-id="element-244" data-mp="3lyyyy90db1f0" />
      <rect x="9" y="1" width="6" height="6" rx="1" data-id="element-245" data-mp="3h1fkp93o6on2" />
      <rect x="1" y="9" width="6" height="6" rx="1" data-id="element-246" data-mp="3vr4yq4wv2n4r" />
      <rect x="9" y="9" width="6" height="6" rx="1" data-id="element-247" data-mp="3vqlhkqf1c2rs" />
    </svg>;
}
function IconTable() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-248" data-mp="3qsjza7pfo7hd">
      <rect x="1" y="2" width="14" height="12" rx="1.5" data-id="element-249" data-mp="332pp5xwi0x0t" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" data-id="element-250" data-mp="2y4619rychb5t" />
      <line x1="1" y1="9" x2="15" y2="9" data-id="element-251" data-mp="2y5h3vog20cvc" />
      <line x1="6" y1="5.5" x2="6" y2="14" data-id="element-252" data-mp="3c3njfoh4xq67" />
    </svg>;
}
function IconTile() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-253" data-mp="3810xu5b251hg">
      <rect x="1" y="1" width="4" height="4" rx="0.5" data-id="element-254" data-mp="rbvd0mzms36o" />
      <rect x="6" y="1" width="4" height="4" rx="0.5" data-id="element-255" data-mp="nbbjdte9nhpa" />
      <rect x="11" y="1" width="4" height="4" rx="0.5" data-id="element-256" data-mp="nas0oagskpa7" />
      <rect x="1" y="6" width="4" height="4" rx="0.5" data-id="element-257" data-mp="3g6bfrgyscykp" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" data-id="element-258" data-mp="3g5jrysbfk54z" />
      <rect x="11" y="6" width="4" height="4" rx="0.5" data-id="element-259" data-mp="3l43hf2p8fz23" />
      <rect x="1" y="11" width="4" height="4" rx="0.5" data-id="element-260" data-mp="3l6poz2c4iekb" />
      <rect x="6" y="11" width="4" height="4" rx="0.5" data-id="element-261" data-mp="3q5aqd53434ac" />
      <rect x="11" y="11" width="4" height="4" rx="0.5" data-id="element-262" data-mp="3u5kyanags4ru" />
    </svg>;
}
function IconGallery() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-263" data-mp="2xfu28jmiy9jx">
      <rect x="1" y="1" width="6" height="7" rx="1" data-id="element-264" data-mp="32f6nizxqz8ke" />
      <rect x="9" y="1" width="6" height="7" rx="1" data-id="element-265" data-mp="36g0ddyur2xfx" />
      <rect x="1" y="10" width="14" height="5" rx="1" data-id="element-266" data-mp="36efgi2kyr2ew" />
    </svg>;
}
function IconWaterfall() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-267" data-mp="3b804r2wr8i0w">
      <line x1="2" y1="1" x2="2" y2="15" data-id="element-268" data-mp="2iqed182n6c6c" />
      <line x1="6" y1="1" x2="6" y2="15" data-id="element-269" data-mp="2ipcqa6c9k18s" />
      <line x1="10" y1="1" x2="10" y2="15" data-id="element-270" data-mp="2iobg0432nis4" />
      <line x1="14" y1="1" x2="14" y2="15" data-id="element-271" data-mp="2b755lkdp4njx" />
      <rect x="1" y="3" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-272" data-mp="2b4so18buof7e" />
      <rect x="5" y="6" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-273" data-mp="268lfd7s2kplr" />
      <rect x="9" y="2" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-274" data-mp="224nf2tr0d8r4" />
      <rect x="13" y="8" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-275" data-mp="1x6c7yqs3lccl" />
    </svg>;
}
const VIEW_OPTIONS: {
  mode: DealViewMode;
  label: string;
  icon: React.FC;
}[] = [{
  mode: 'cards',
  label: 'Cards',
  icon: IconCards
}, {
  mode: 'table',
  label: 'Table',
  icon: IconTable
}, {
  mode: 'tile',
  label: 'Tile',
  icon: IconTile
}, {
  mode: 'gallery',
  label: 'Gallery',
  icon: IconGallery
}, {
  mode: 'waterfall',
  label: 'Waterfall',
  icon: IconWaterfall
}];

/* ─── Shared request-name link with icon ───────────────────────────────── */

const LINK_STYLE: React.CSSProperties = {
  color: '#1d4ed8',
  textDecoration: 'none'
};
function RequestLink({
  d,
  maxLen = 35
}: {
  d: Deal;
  maxLen?: number;
}) {
  return <a href={getURL(d)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} title={d.requestName} data-id="element-276" data-mp="39jj3fffp4259">
      <svg viewBox="0 0 12 12" fill="none" stroke="#1d4ed8" strokeWidth="1.4" strokeLinecap="round" style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      marginRight: 3,
      verticalAlign: 'middle',
      position: 'relative',
      top: -1
    }} data-id="element-277" data-mp="2uvo8rti4nnpj">
        <path d="M5 1.5H3.5A2 2 0 0 0 1.5 3.5v5A2 2 0 0 0 3.5 10.5h5a2 2 0 0 0 2-2V8.5" data-id="element-278" data-mp="2lswsngeb43rk" />
        <path d="M7.5 1.5h3v3" data-id="element-279" data-mp="2pxcxdec6p557" />
        <path d="M5.5 6.5 10.5 1.5" data-id="element-280" data-mp="2ptgtaty39pig" />
      </svg>
      {d.requestName.substring(0, maxLen)}
    </a>;
}

/* ─── View switcher bar ────────────────────────────────────────────────── */

function ViewSwitcher({
  active,
  onChange
}: {
  active: DealViewMode;
  onChange: (m: DealViewMode) => void;
}) {
  return <div className="view-switcher" data-id="element-281" data-mp="f3qji3uljbxv">
      {VIEW_OPTIONS.map(opt => <button key={opt.mode} className={active === opt.mode ? 'active' : ''} onClick={() => onChange(opt.mode)} title={opt.label} data-id="element-282" data-mp="f2yyto2jeymd">
          <opt.icon data-id="element-283" data-mp="1uo9d392x5ryj" />
          {opt.label}
        </button>)}
    </div>;
}

/* ─── Age bucket helper for waterfall ──────────────────────────────────── */

function ageBucket(age: number | null): string {
  if (age == null) return 'Unknown';
  if (age <= 30) return '0–30 days';
  if (age <= 60) return '31–60 days';
  if (age <= 90) return '61–90 days';
  return '90+ days';
}
const AGE_BUCKETS = ['0–30 days', '31–60 days', '61–90 days', '90+ days'];
function groupByAgeBucket(deals: Deal[]): Record<string, Deal[]> {
  const groups: Record<string, Deal[]> = {};
  for (const b of AGE_BUCKETS) groups[b] = [];
  for (const d of deals) {
    const b = ageBucket(d.age);
    if (!groups[b]) groups[b] = [];
    groups[b].push(d);
  }
  return groups;
}

/* ─── Individual view renderers ────────────────────────────────────────── */

function TableView({
  deals
}: {
  deals: Deal[];
}) {
  return <table className="deal-table" data-id="element-284" data-mp="1inb0qso9qvpo">
      <thead data-id="element-285" data-mp="1mn3a37lhl5w3">
        <tr data-id="element-286" data-mp="1mmjvo0v1ozmt">
          <th style={{
          width: '9%'
        }} data-id="element-287" data-mp="1mmbjycans0d7">ID</th>
          <th style={{
          width: '18%'
        }} data-id="element-288" data-mp="1mp621x67993g">Account</th>
          <th style={{
          width: '20%'
        }} data-id="element-289" data-mp="1rlwpj5kc36yw">Request</th>
          <th style={{
          width: '8%'
        }} data-id="element-290" data-mp="1ro94raypj389">Age</th>
          <th style={{
          width: '10%'
        }} data-id="element-291" data-mp="1wlj35g42qp88">GNARR</th>
          <th style={{
          width: '10%'
        }} data-id="element-292" data-mp="1wj6ekk3t9gm7">Close Q</th>
          <th style={{
          width: '13%'
        }} data-id="element-293" data-mp="21l5flnc2w1fz">Offering</th>
          <th style={{
          width: '12%'
        }} data-id="element-294" data-mp="21ijbjxoec42i">Sub Region</th>
        </tr>
      </thead>
      <tbody data-id="element-295" data-mp="25k4kj7xt7y7f">
        {deals.map(d => <tr key={d.id} data-id="element-296" data-mp="2acxq1y13rakk">
            <td style={{
          color: '#8892a0',
          fontSize: 9
        }} data-id="element-297" data-mp="2adp8s89lpdtf">{d.id}</td>
            <td title={d.account} data-id="element-298" data-mp="2fbhdk2vcguf1">{d.account.substring(0, 24)}</td>
            <td title={d.requestName} data-id="element-299" data-mp="2fdv541qtfohu">
              <RequestLink d={d} maxLen={30} data-id="element-300" data-mp="2kbdgdysb2ft0" />
            </td>
            <td className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
        }} data-id="element-301" data-mp="2k8r09arcmrz6">
              {formatAge(d.age)}
            </td>
            <td data-id="element-302" data-mp="8myvwspykncq">{fmtG(d.gnarr)}</td>
            <td data-id="element-303" data-mp="8mp02oi8flz4">{d.close_q}</td>
            <td data-id="element-304" data-mp="8puu8i5pxe1p"><span className="badge b-blue" style={{
            fontSize: 8
          }} data-id="element-305" data-mp="8pusodq2l5zl">{d.offering.substring(0, 18)}</span></td>
            <td data-id="element-306" data-mp="3uuq404hyjc27"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-307" data-mp="3uuq5k8xlvk4b">{d.sub_region}</span></td>
          </tr>)}
      </tbody>
    </table>;
}
function TileView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-tile-grid" data-id="element-308" data-mp="rdg8nnueb8cg">
      {deals.map(d => <div key={d.id} className="deal-tile" title={`${d.account}\n${d.requestName}\nAge: ${formatAge(d.age)}\nGNARR: ${fmtG(d.gnarr)}`} data-id="element-309" data-mp="rg2qqhewx68x">
          <div className="dt-id" data-id="element-310" data-mp="hoq0fu8rio50">{d.id}</div>
          <div className="dt-acct" data-id="element-311" data-mp="hnwpsi6onehl">{d.account.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-312" data-mp="1f4lzey1vjl1j">
            <RequestLink d={d} maxLen={16} data-id="element-313" data-mp="1ad3ww5k7dceg" />
          </div>
          <span className={`dt-age ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-314" data-mp="1abj4omld25jr">
            {formatAge(d.age)}
          </span>
        </div>)}
    </div>;
}
function GalleryView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-gallery" data-id="element-315" data-mp="9q5t47g8s359">
      {deals.map(d => <div key={d.id} className="deal-gallery-card" data-id="element-316" data-mp="en5ui525chq1">
          <div className="dg-id" data-id="element-317" data-mp="ell3uqiydixg">{d.id}</div>
          <div className="dg-title" data-id="element-318" data-mp="12d08wr246ece">{d.account}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-319" data-mp="12b5ln63dea55">
            <RequestLink d={d} maxLen={40} data-id="element-320" data-mp="16dtydov7g17e" />
          </div>
          <div className="dg-meta" data-id="element-321" data-mp="sf2lg5s2wlku">
            <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-322" data-mp="se0zh69iyeoc">{formatAge(d.age)}</span>
            <span className="badge b-blue" data-id="element-323" data-mp="3d5b9nb4d9nfy">{fmtG(d.gnarr)}</span>
            <span className="badge b-purple" style={{
          fontSize: 8
        }} data-id="element-324" data-mp="3d404p7z6q9n9">{d.close_q}</span>
            {isAged(d) && <span className="badge b-red" data-id="element-325" data-mp="3i5phas222vdg">⚠ aged</span>}
          </div>
          <div className="dg-footer" data-id="element-326" data-mp="3ajtwup7g9z1f">
            {d.sub_region} · {d.offering.substring(0, 26)}
          </div>
        </div>)}
    </div>;
}
function WaterfallView({
  deals
}: {
  deals: Deal[];
}) {
  const groups = groupByAgeBucket(deals);
  return <div className="deal-waterfall" data-id="element-327" data-mp="3emgtd3f2g84y">
      {AGE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-328" data-mp="2qxxn0tfku5fi">
            <div className="deal-waterfall-lane-header" data-id="element-329" data-mp="2mvkj8ake4hld">
              <span data-id="element-330" data-mp="2mwvjw1ijh989">{bucket}</span>
              <span className="lane-count" data-id="element-331" data-mp="30mpc8tt96bmq">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-332" data-mp="30osbm4gaph44">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-333" data-mp="2vtw8a85lz7bp">none</div>}
              {items.map(d => <div key={d.id} className="deal-waterfall-card" data-id="element-334" data-mp="2371ntb8sk7y8">
                  <div className="dw-id" data-id="element-335" data-mp="9mpf4welqsra">{d.id}</div>
                  <div className="dw-acct" title={d.account} data-id="element-336" data-mp="9k3c9a2np1fe">{d.account.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-337" data-mp="4l9zjjv71ega">
                    <RequestLink d={d} maxLen={20} data-id="element-338" data-mp="3vs2ysi6ff3i6" />
                  </div>
                  <div className="dw-tags" data-id="element-339" data-mp="3rr0wttl4kdci">
                    <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} style={{
                fontSize: 8
              }} data-id="element-340" data-mp="3ruex8wa8achb">{formatAge(d.age)}</span>
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-341" data-mp="3i06akp7vpdr2">{fmtG(d.gnarr)}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Dispatch view ────────────────────────────────────────────────────── */

function DealExpandedView({
  deals,
  mode
}: {
  deals: Deal[];
  mode: DealViewMode;
}) {
  if (deals.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '8px 0',
      fontStyle: 'italic'
    }} data-id="element-342" data-mp="1elmeicn7x36y">No in-progress deals.</div>;
  }
  switch (mode) {
    case 'table':
      return <TableView deals={deals} data-id="element-343" data-mp="1jimbzz604xmg" />;
    case 'tile':
      return <TileView deals={deals} data-id="element-344" data-mp="1nlsp9qrj7p2f" />;
    case 'gallery':
      return <GalleryView deals={deals} data-id="element-345" data-mp="1nk7txyxe823i" />;
    case 'waterfall':
      return <WaterfallView deals={deals} data-id="element-346" data-mp="1sju7yal1pmdd" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-347" data-mp="vvw96vltwz0s">
          {deals.map(d => <div key={d.id} className="deal-chip" data-id="element-348" data-mp="zwg8o5tvkeq3">
              <div className="dc-id" data-id="element-349" data-mp="zwzo9fw1yr0y">{d.id}</div>
              <div className="dc-name" data-id="element-350" data-mp="14vt3bcqzmq38">{d.account.substring(0, 35)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 3
          }} data-id="element-351" data-mp="14tgdyeiwhdg5"><RequestLink d={d} data-id="element-352" data-mp="19mswq80weq0z" /></div>
              <div className="dc-tags" data-id="element-353" data-mp="19nkk4vkcdhg6">
                <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-354" data-mp="c75c70jk56hr">{formatAge(d.age)}</span>
                <span className="badge b-blue" data-id="element-355" data-mp="h3vrvmrga02n">{fmtG(d.gnarr)}</span>
                <span className="badge b-purple" style={{
              fontSize: 8
            }} data-id="element-356" data-mp="1kfl133ec2bru">{d.close_q}</span>
                {isAged(d) && <span className="badge b-red" data-id="element-357" data-mp="1fh00h2v65q2v">⚠ aged</span>}
              </div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-358" data-mp="1t9fvphhtwjt8">
                {d.sub_region} · {d.offering.substring(0, 26)}
              </div>
            </div>)}
        </div>;
  }
}

/* ─── SC Matrix (unchanged) ───────────────────────────────────────────── */

function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    const bkts = bucketProducts(d.products || '');
    for (const scName of splitSCNames(d.sc)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + d.id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-359" data-mp="27aafq5aydpe2">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-360" data-mp="27b3cqek410j0">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-361" data-mp="1y9vqiuuvbq5a">
        <tr data-id="element-362" data-mp="1ybgajqjk3qow">
          <th data-id="element-363" data-mp="1yb6tisghotvc">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-364" data-mp="1tb29wtkabj0w">{c}</th>)}
          <th data-id="element-365" data-mp="1tc3q1cg5qvph">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-366" data-mp="2qyj7ie8l3td1">
        {scRows.map(row => <tr key={row.full} data-id="element-367" data-mp="2qxhrdvcpogog">
            <td title={row.full} data-id="element-368" data-mp="2lyy7s1llbgz9">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-369" data-mp="2m115l7szieej">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-370" data-mp="2h6n7uyqunv6p">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-371" data-mp="2c9n26oxwwug4">—</span>}
              </td>)}
            <td data-id="element-372" data-mp="39o93h4o32l6j"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-373" data-mp="39p0x49y4e8u6">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-374" data-mp="34whid9np57ns">
        <tr data-id="element-375" data-mp="34vpqa8tb5s29">
          <td data-id="element-376" data-mp="3c8ynsomp8rz0">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-377" data-mp="3cai4uhg6j71u"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-378" data-mp="3caru22teha6h">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-379" data-mp="3ha4i2qw0cnan"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-380" data-mp="3ha4jmvbnovcr">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}

/* ─── Main panel ───────────────────────────────────────────────────────── */

export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const [dealView, setDealView] = useState<DealViewMode>('cards');
  const [scSearch, setScSearch] = useState('');
  const {
    deals
  } = useDeals();
  const scDataAll = buildSCList(state.r, state.sub, state, deals);
  const scFiltered = scSearch ? scDataAll.filter(s => s.n.toLowerCase().includes(scSearch.toLowerCase())) : scDataAll;
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(d => splitSCNames(d.sc))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allIP);
  const maxIP = Math.max(...scFiltered.map(s => s.ip), 1);
  const maxClosed = Math.max(...scFiltered.map(s => s.closed), 1);
  const scColumns: SortableColumn<SCData>[] = [{
    key: 'n',
    label: 'SC Name',
    width: '22%',
    render: p => <span className="name-link" data-id="element-381" data-mp="1d4yvbt67rivo">
          {p.n}
          <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-382" data-mp="1d6jp3gkpexsh">▾</span>
        </span>
  }, {
    key: 'ip',
    label: 'In-progress',
    width: '14%',
    sortValue: p => p.ip,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-383" data-mp="tayw7t1405wh">
            <div className="pf" style={{
          width: `${Math.round(p.ip / maxIP * 100)}%`
        }} data-id="element-384" data-mp="ta710jbfca6q" />
          </div>
          <strong data-id="element-385" data-mp="y7fjkl0hig8i">{p.ip}</strong>
        </>
  }, {
    key: 'closed',
    label: 'Closed',
    width: '14%',
    sortValue: p => p.closed,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-386" data-mp="ocxcd5vv02jy">
            <div className="pf" style={{
          width: `${Math.round(p.closed / maxClosed * 100)}%`,
          background: '#34d399'
        }} data-id="element-387" data-mp="ac4fwflzyxe7" />
          </div>
          <strong data-id="element-388" data-mp="fb7mw5ljfdot">{p.closed}</strong>
        </>
  }, {
    key: 'avgAge',
    label: 'Avg age',
    width: '11%',
    sortValue: p => p.avgAge,
    render: p => <span style={{
      color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
      fontWeight: p.avgAge > 40 ? 600 : 400
    }} data-id="element-389" data-mp="29oytsxchfjgk">
          {p.avgAge}d
        </span>
  }, {
    key: 'med',
    label: 'Median age',
    width: '11%',
    sortValue: p => p.med,
    render: p => <>{p.med}d</>
  }, {
    key: 'gnarr',
    label: 'GNARR (IP)',
    width: '14%',
    sortValue: p => p.gnarr,
    render: p => <>{fmtG(p.gnarr)}</>
  }, {
    key: 'closedGnarr',
    label: 'GNARR (Closed)',
    width: '14%',
    sortValue: p => p.closedGnarr,
    render: p => <>{fmtG(p.closedGnarr)}</>
  }];
  return <div data-id="element-390" data-mp="2ui8m5lggfdgw">
      {/* Metrics */}
      <div className="mrow" data-id="element-391" data-mp="2pkgyj3muedii">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-392" data-mp="2pjp2xst8wfs8" />
        <MetricCard label="Requests / person" value={scFiltered.length ? +(allIP.length / scFiltered.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-393" data-mp="2lk6p1gzud4yw" />
        <MedianAgeCard ages={ages} data-id="element-394" data-mp="jr8t3carpw9q" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-395" data-mp="js0ha221crpz" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — deals (click row to expand)" data-id="element-396" data-mp="21n14qcp8ba99" />
      <div className="tw" data-id="element-397" data-mp="26mx6e5agepln">
        <div style={{
        padding: '8px 12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-id="element-398" data-mp="26m3wwwk41lzt">
          <input type="text" placeholder="Search SC name…" value={scSearch} onChange={e => {
          setScSearch(e.target.value);
          setExpandedSC(null);
        }} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          width: 160,
          color: '#344054',
          background: '#fff'
        }} data-id="element-399" data-mp="2fgijzlavu0th" />
          {expandedSC && <ViewSwitcher active={dealView} onChange={setDealView} data-id="element-400" data-mp="1wr3mg0uenttu" />}
        </div>
        <SortableTable columns={scColumns} data={scFiltered} rowKey={p => p.n} defaultSort={{
        key: 'ip',
        dir: 'desc'
      }} onRowClick={p => setExpandedSC(expandedSC === p.n ? null : p.n)} renderExpandedRow={p => expandedSC === p.n ? <tr key={`exp-${p.n}`} data-id="element-402" data-mp="3ekqcva3ej17d">
              <td colSpan={7} style={{
          padding: 0
        }} data-id="element-403" data-mp="3ejp1t5mdyepn">
                <div className="exp-inner" data-id="element-404" data-mp="3jc03oe0osaku">
                  <div style={{
              fontSize: 9,
              fontWeight: 700,
              color: '#667085',
              marginBottom: 6,
              textTransform: 'uppercase',
              letterSpacing: '.05em'
            }} data-id="element-405" data-mp="3jd1hurd01d6s">
                    In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)
                  </div>
                  <DealExpandedView deals={p.deals} mode={dealView} data-id="element-406" data-mp="2vlm8iemt1vm1" />
                  {p.closedDeals.length > 0 && <>
                      <div style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#059669',
                margin: '12px 0 6px',
                textTransform: 'uppercase',
                letterSpacing: '.05em'
              }} data-id="element-407" data-mp="30jntj53sk505">
                        Closed — {p.n} ({p.closed} deals)
                      </div>
                      <DealExpandedView deals={p.closedDeals} mode={dealView} data-id="element-408" data-mp="ktujuuniugol" />
                    </>}
                </div>
              </td>
            </tr> : null} data-id="element-401" data-mp="336mdgzucwk6r" />
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-409" data-mp="20dkala00arce" />
      <div className="tw" data-id="element-410" data-mp="1vladna0h4r0m">
        <div className="sub" data-id="element-411" data-mp="1vkhfuyjhtbum">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-412" data-mp="1lnwb8ebdy2rb">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-413" data-mp="1lpphhsi36kys">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-414" data-mp="1hlhpnmgc0owc" />
          </table>
        </div>
      </div>
    </div>;
}